<script setup lang="tsx">
import type { Risk } from '@/models/api-generated'
import OrderLabel from '../widgets/OrderLabel.vue'
import RiskRow from './RiskRow.vue'
import useRiskColumns from './columns'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import PlusButton from '../widgets/PlusButton.vue'

const props = defineProps<{
	component: string
	isEdit: boolean
	businessProcess: number
	rowClass: (i: number) => Record<string, boolean>
}>()

const items = defineModel<Risk[]>('items', { required: true })
const deletedRiskIds = defineModel<Set<number>>('deletedRiskIds', { required: true })
const deletedMetaIds = defineModel<Set<number>>('deletedMetaIds', { required: true })

const { columns } = useRiskColumns(props.component)
const scrollContainer = ref<HTMLElement | null>(null)
const showLeft = ref(false)
const showRight = ref(false)
const isAllExpanded = ref(false)

watch(isAllExpanded, (value) => {
	nextTick(() => {
		for (let a of items.value ?? []) {
			a.isExpanded = value
		}
	})
})

function updateArrows() {
	if (props.businessProcess) return
	const el = scrollContainer.value
	if (!el) return
	const overshoot = 1
	const canScroll = el.scrollWidth > el.clientWidth
	showLeft.value = canScroll && el.scrollLeft > overshoot
	showRight.value = canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - overshoot
}

function scrollLeft() {
	scrollContainer.value?.scrollBy({ left: -scrollContainer.value.clientWidth, behavior: 'smooth' })
}

function scrollRight() {
	scrollContainer.value?.scrollBy({ left: scrollContainer.value.clientWidth, behavior: 'smooth' })
}

const resizeObserver = new ResizeObserver(() => updateArrows())

const removeNew = (index: number) => {
	items.value.splice(index, 1)
}

const toggleDeletedRisks = (id: number) => {
	if (deletedRiskIds.value.has(id)) {
		deletedRiskIds.value.delete(id)
	} else {
		deletedRiskIds.value.add(id)
	}
}

onMounted(async () => {
	await nextTick()
	const el = scrollContainer.value
	if (el) {
		el.addEventListener('scroll', updateArrows)
		resizeObserver.observe(el)
	}
	updateArrows()
})

onBeforeUnmount(() => {
	const el = scrollContainer.value
	if (el) {
		el.removeEventListener('scroll', updateArrows)
		resizeObserver.disconnect()
	}
})
defineExpose({ scrollContainer })
</script>

<template>
	<div class="table-responsive w-100" ref="scrollContainer">
		<table class="table">
			<div v-show="showLeft" class="scroll-arrow" @click="scrollLeft"><i class="bi bi-arrow-left-short"></i></div>
			<thead>
				<tr>
					<th class="w-auto-32"><PlusButton v-model="isAllExpanded" /></th>
					<th v-if="columns.code.value.isVisible">
						<OrderLabel v-model="columns.code.value">{{ columns.code.value.label }}</OrderLabel>
					</th>
					<th v-if="columns.name.value.isVisible">
						<OrderLabel v-model="columns.name.value">{{ columns.name.value.label }}</OrderLabel>
					</th>
					<th v-if="columns.catalog.value.isVisible">
						<OrderLabel v-model="columns.catalog.value">{{ columns.catalog.value.label }}</OrderLabel>
					</th>
					<th v-if="columns.category.value.isVisible">
						<OrderLabel v-model="columns.category.value">{{ columns.category.value.label }}</OrderLabel>
					</th>
					<th v-if="columns.cia.value.isVisible">{{ columns.cia.value.label }}</th>
					<th v-if="columns.connections.value.isVisible">{{ columns.connections.value.label }}</th>
					<th v-if="columns.score.value.isVisible">
						<OrderLabel v-model="columns.score.value">{{ columns.score.value.label }}</OrderLabel>
					</th>
					<th v-if="columns.residual.value.isVisible">
						<OrderLabel v-model="columns.residual.value">{{ columns.residual.value.label }}</OrderLabel>
					</th>
				</tr>
			</thead>
			<tbody>
				<RiskRow
					v-for="(r, i) in items"
					:key="r.id"
					v-model="items[i]"
					:class="props.rowClass(i)"
					:is-edit="props.isEdit || !r.id"
					:on-risk-delete="r.id ? () => toggleDeletedRisks(r.id) : () => removeNew(i)"
					v-model:deleted-meta-ids="deletedMetaIds"
				>
				</RiskRow>
			</tbody>
			<div v-show="showRight" class="scroll-arrow right" @click="scrollRight"><i class="bi bi-arrow-right-short"></i></div>
		</table>
	</div>
</template>
